import React, { useState } from 'react';
import { Button, TextInput, Select } from '@etherisc/ui-kit';
import { ClientSelect } from './ClientSelect';
import { CostCenterSelect } from './CostCenterSelect';
import { useRecordPremiumDue } from '../../hooks/insurance/useInsurancePremium';
import type { PremiumDueRequest, InsuranceJournal } from '../../types/Insurance';

interface PremiumFormData {
  clientId: string;
  costCenterId: string;
  amount: string;
  currency: string;
  dueDate: string; // ISO date string
  description?: string;
}

interface InsurancePremiumFormProps {
  onSuccess?: (journal: InsuranceJournal) => void;
  onCancel: () => void;
  initialData?: Partial<PremiumFormData>;
}

export const InsurancePremiumForm: React.FC<InsurancePremiumFormProps> = ({ 
  onSuccess, 
  onCancel,
  initialData 
}) => {
  const [formData, setFormData] = useState<PremiumFormData>({
    clientId: initialData?.clientId || '',
    costCenterId: initialData?.costCenterId || '',
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'EUR',
    dueDate: initialData?.dueDate || new Date().toISOString().split('T')[0],
    description: initialData?.description || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const recordPremiumDue = useRecordPremiumDue();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.costCenterId) {
      newErrors.costCenterId = 'Policy/Cost Center is required';
    }

    if (!formData.amount) {
      newErrors.amount = 'Premium amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Premium amount must be a positive number';
      }
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    } else if (!/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a valid 3-letter code (e.g., EUR, USD)';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const request: PremiumDueRequest = {
        clientId: formData.clientId,
        costCenterId: formData.costCenterId,
        amount: formData.amount,
        currency: formData.currency,
        dueDate: formData.dueDate,
        description: formData.description || undefined
      };

      const result = await recordPremiumDue.mutateAsync(request);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Reset form
      setFormData({
        clientId: '',
        costCenterId: '',
        amount: '',
        currency: 'EUR',
        dueDate: new Date().toISOString().split('T')[0],
        description: ''
      });
      
    } catch (error) {
      console.error('Failed to record premium due:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to record premium due' });
    }
  };

  const updateFormData = (field: keyof PremiumFormData) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const actualValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => ({ ...prev, [field]: actualValue }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ClientSelect 
          label="Client" 
          value={formData.clientId} 
          onChange={updateFormData('clientId')}
          required
        />
        {errors.clientId && (
          <div className="text-red-500 text-sm mt-1">{errors.clientId}</div>
        )}
        
        <CostCenterSelect 
          label="Policy/Cost Center" 
          clientId={formData.clientId}
          value={formData.costCenterId} 
          onChange={updateFormData('costCenterId')}
          required
        />
        {errors.costCenterId && (
          <div className="text-red-500 text-sm mt-1">{errors.costCenterId}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextInput 
          label="Premium Amount" 
          type="text"
          value={formData.amount} 
          onChange={updateFormData('amount')}
          placeholder="0.00"
          required
        />
        {errors.amount && (
          <div className="text-red-500 text-sm mt-1">{errors.amount}</div>
        )}
        
        <Select 
          label="Currency" 
          value={formData.currency} 
          onValueChange={updateFormData('currency')}
          required
          options={[
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
            { value: 'GBP', label: 'GBP' }
          ]}
        />
        {errors.currency && (
          <div className="text-red-500 text-sm mt-1">{errors.currency}</div>
        )}
        
        <TextInput 
          label="Due Date" 
          type="date"
          value={formData.dueDate} 
          onChange={updateFormData('dueDate')}
          required
        />
        {errors.dueDate && (
          <div className="text-red-500 text-sm mt-1">{errors.dueDate}</div>
        )}
      </div>

      <TextInput 
        label="Description (Optional)" 
        type="text"
        value={formData.description || ''} 
        onChange={updateFormData('description')}
        placeholder="Additional notes about this premium..."
      />

      {errors.submit && (
        <div className="text-red-500 text-sm p-3 bg-red-50 rounded-md">
          {errors.submit}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-4">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={recordPremiumDue.isPending}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={recordPremiumDue.isPending}
        >
          {recordPremiumDue.isPending ? 'Recording...' : 'Record Premium Due'}
        </Button>
      </div>
    </form>
  );
};
